/**
 * Revision Service
 *
 * Pure business logic for revision log operations.
 * No React or Firebase dependencies.
 */

import { RevisionLog, RevisionType } from '@/types';

// ============================================
// Types
// ============================================

export interface RevisionSummary {
  totalRevisions: number;
  moveCount: number;
  deleteCount: number;
  resizeCount: number;
  editCount: number;
  addCount: number;
  mostRevisedBlock: { blockId: string; blockTitle: string; count: number } | null;
}

export interface AddRevisionInput {
  type: RevisionType;
  blockId: string;
  blockTitle: string;

  // For moves
  fromStartTime?: number;
  fromDuration?: number;
  toStartTime?: number;
  toDuration?: number;

  // For resizes
  oldDuration?: number;
  newDuration?: number;

  // For deletes
  deletedStartTime?: number;
  deletedDuration?: number;

  // For adds
  addedStartTime?: number;
  addedDuration?: number;

  // Optional context
  reason?: string;
  planVersion?: number;
}

// ============================================
// ID Generation
// ============================================

/**
 * Generate a unique revision ID.
 *
 * @returns Unique revision ID string
 */
export function generateRevisionId(): string {
  return `rev_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

// ============================================
// Revision Sorting
// ============================================

/**
 * Sort revisions by timestamp (newest first).
 *
 * @param revisions - Array of revisions to sort
 * @returns New sorted array (does not mutate original)
 */
export function sortRevisionsByTimestamp(revisions: RevisionLog[]): RevisionLog[] {
  return [...revisions].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
}

/**
 * Sort revisions by timestamp (oldest first).
 *
 * @param revisions - Array of revisions to sort
 * @returns New sorted array (does not mutate original)
 */
export function sortRevisionsByTimestampAsc(revisions: RevisionLog[]): RevisionLog[] {
  return [...revisions].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );
}

// ============================================
// Revision Summary
// ============================================

/**
 * Calculate a summary of revision activity.
 *
 * @param revisions - Array of revisions to analyze
 * @returns Summary object with counts and most revised block
 */
export function calculateRevisionSummary(revisions: RevisionLog[]): RevisionSummary {
  const moveCount = revisions.filter((r) => r.type === 'move').length;
  const deleteCount = revisions.filter((r) => r.type === 'delete').length;
  const resizeCount = revisions.filter((r) => r.type === 'resize').length;
  const editCount = revisions.filter((r) => r.type === 'edit').length;
  const addCount = revisions.filter((r) => r.type === 'add').length;

  // Find most revised block
  const blockCounts = new Map<string, { blockTitle: string; count: number }>();
  for (const revision of revisions) {
    const existing = blockCounts.get(revision.blockId);
    if (existing) {
      existing.count++;
    } else {
      blockCounts.set(revision.blockId, { blockTitle: revision.blockTitle, count: 1 });
    }
  }

  let mostRevisedBlock: { blockId: string; blockTitle: string; count: number } | null = null;
  let maxCount = 0;
  for (const [blockId, data] of blockCounts.entries()) {
    if (data.count > maxCount) {
      maxCount = data.count;
      mostRevisedBlock = { blockId, blockTitle: data.blockTitle, count: data.count };
    }
  }

  return {
    totalRevisions: revisions.length,
    moveCount,
    deleteCount,
    resizeCount,
    editCount,
    addCount,
    mostRevisedBlock,
  };
}

// ============================================
// Revision Building
// ============================================

/**
 * Build a revision log entry from input data.
 * Only includes fields that have defined values.
 *
 * @param date - The date string (YYYY-MM-DD)
 * @param data - The revision input data
 * @returns RevisionLog object ready for storage
 */
export function buildRevisionLog(date: string, data: AddRevisionInput): RevisionLog {
  return {
    id: generateRevisionId(),
    date,
    timestamp: new Date(),
    type: data.type,
    blockId: data.blockId,
    blockTitle: data.blockTitle,
    ...(data.fromStartTime !== undefined && { fromStartTime: data.fromStartTime }),
    ...(data.fromDuration !== undefined && { fromDuration: data.fromDuration }),
    ...(data.toStartTime !== undefined && { toStartTime: data.toStartTime }),
    ...(data.toDuration !== undefined && { toDuration: data.toDuration }),
    ...(data.oldDuration !== undefined && { oldDuration: data.oldDuration }),
    ...(data.newDuration !== undefined && { newDuration: data.newDuration }),
    ...(data.deletedStartTime !== undefined && { deletedStartTime: data.deletedStartTime }),
    ...(data.deletedDuration !== undefined && { deletedDuration: data.deletedDuration }),
    ...(data.addedStartTime !== undefined && { addedStartTime: data.addedStartTime }),
    ...(data.addedDuration !== undefined && { addedDuration: data.addedDuration }),
    ...(data.reason !== undefined && { reason: data.reason }),
    ...(data.planVersion !== undefined && { planVersion: data.planVersion }),
  };
}

// ============================================
// Revision Filtering
// ============================================

/**
 * Filter revisions by type.
 *
 * @param revisions - Array of revisions to filter
 * @param type - Revision type to filter by
 * @returns Filtered array of revisions
 */
export function filterRevisionsByType(revisions: RevisionLog[], type: RevisionType): RevisionLog[] {
  return revisions.filter((r) => r.type === type);
}

/**
 * Get revisions for a specific block.
 *
 * @param revisions - Array of revisions to filter
 * @param blockId - Block ID to filter by
 * @returns Filtered array of revisions for the block
 */
export function getRevisionsForBlock(revisions: RevisionLog[], blockId: string): RevisionLog[] {
  return revisions.filter((r) => r.blockId === blockId);
}
